// Seeded procedural generation of a hex-island world. Islands are seeded on a REGULAR POLYGON centred
// on the origin. 3 factions → an upward triangle (one island on top, two below), 4 → an axis-aligned
// square, 5 → a pentagon, so the archipelago always reads as a clean, symmetric shape at equal
// spacing. Each island then grows into a small equal-size blob with a sea buffer, and adjacent islands
// are wired together by a sea-link ring (each island reaches its two nearest neighbours).

import { mulberry32 } from '../engine/rng';
import { hexNeighbors, hexKey, hexDistance, pixelToAxial } from './hex';
import { NEUTRAL, type Region, type RegionId, type Island } from './types';

const ISLAND_SIZE = 11; // target regions per island, every island is this size, so no faction starts with a territory edge

// resource pairs per island (indices into Gold/Wood/Stone/Food); spread so all 4 recur. Mostly
// cosmetic: createGame overrides each home island with its race's signature pair.
const RES_COMBOS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [2, 3],
  [0, 2],
  [1, 3],
  [0, 3],
  [1, 2],
];

// Orientation of the seed polygon per faction count, chosen so the shape reads naturally on screen
// (y grows downward): 3 → one island at the top and two below, 4 → a flat-top square, 5 → a pentagon
// with a single island at the top. Other counts fall back to "a vertex pointing up".
const START_ANGLE: Record<number, number> = {
  3: -Math.PI / 2,
  4: (-3 * Math.PI) / 4,
  5: -Math.PI / 2,
};

// axial -> cube line between two hexes (cube lerp + round), for carving land corridors
function hexLine(a: { q: number; r: number }, b: { q: number; r: number }): { q: number; r: number }[] {
  const ax = a.q;
  const az = a.r;
  const ay = -ax - az;
  const bx = b.q;
  const bz = b.r;
  const by = -bx - bz;
  const steps = Math.max(Math.abs(ax - bx), Math.abs(ay - by), Math.abs(az - bz));
  const out: { q: number; r: number }[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = steps === 0 ? 0 : i / steps;
    const x = ax + (bx - ax) * t;
    const y = ay + (by - ay) * t;
    const z = az + (bz - az) * t;
    let rx = Math.round(x);
    let ry = Math.round(y);
    let rz = Math.round(z);
    const dx = Math.abs(rx - x);
    const dy = Math.abs(ry - y);
    const dz = Math.abs(rz - z);
    if (dx > dy && dx > dz) rx = -ry - rz;
    else if (dy > dz) ry = -rx - rz;
    else rz = -rx - ry;
    out.push({ q: rx, r: rz });
  }
  return out;
}

/** The neutral wilds province on land maps sits at index = factionCount (island id === faction id
 *  stays true for every real faction). Its tiles yield Wood + Stone: the wilderness pays in timber
 *  and rock, distinct from every race's home pair so expansion into the middle has a flavour. */
export const WILDS_RESOURCES: readonly [number, number] = [1, 2];

export function generateWorld(seed: number, factionCount: number, islandSize: number = ISLAND_SIZE, mapKind: 'isles' | 'land' = 'isles', cliffs = false, heart = false): { regions: Region[]; islands: Island[] } {
  const rng = mulberry32(seed);
  const N = factionCount;

  // 1. seed the islands on a regular polygon.
  // adjacentSpacing = the hex distance we want between neighbouring island centres, it grows with the
  // island size so bigger islands still get a clean sea channel and room to reach full size. Rpix is the
  // pixel circumradius of the polygon that yields that spacing (adjacent hexes are √3 px apart at size 1).
  // floor at 8 hexes: on the smallest islands a tighter radius makes the hex-snapping of the polygon
  // vertices relatively coarse (uneven sides), so we keep a minimum that snaps to a clean shape.
  // LAND maps stretch the polygon harder with the size setting: the sqrt curve rounds sizes 7 and 9
  // to the SAME spacing, which made Small/Medium/Large mainlands nearly identical (measured 59/63/80
  // walkable at 3p). Linear spacing gives each setting a genuinely different continent; isles keep
  // the classic curve (the sea gaps already separate their sizes visually).
  const adjacentSpacing = Math.max(8, mapKind === 'land' ? islandSize + 1 : Math.round(2 * Math.sqrt(islandSize) + 2));
  const HEX_UNIT = Math.sqrt(3);
  const Rpix = N <= 1 ? 0 : (adjacentSpacing * HEX_UNIT) / (2 * Math.sin(Math.PI / N));
  const startAngle = START_ANGLE[N] ?? -Math.PI / 2;
  // LAYOUT JITTER: the polygon used to be rigid (only which vertex you started on varied), so every
  // board of a faction count looked identical: same star continent, same island wheel. Each seed now
  // rolls a continuous global rotation plus per-vertex radius (+/-15%) and angle (+/-0.12 rad) noise.
  // Island SIZES stay strictly equal (fairness is tile count, not silhouette), and a minimum pairwise
  // distance guard rerolls a cramped layout so sea channels / land bridges keep working.
  // VARIETY IS OPT-IN, exactly like the cliffs: scripted worlds (tutorial seeds, campaign levels)
  // were vetted and budgeted on the rigid layouts and must not shift underneath their scripts. The
  // classic branch below draws the exact old rng sequence, so cliffs=false worlds stay byte-identical.
  const variety = cliffs;
  const rot = Math.floor(rng() * N);
  let seeds: { q: number; r: number }[] = [];
  if (!variety) {
    for (let i = 0; i < N; i++) {
      const a = startAngle + (2 * Math.PI * ((i + rot) % N)) / N;
      seeds.push(pixelToAxial(Rpix * Math.cos(a), Rpix * Math.sin(a), 1));
    }
  } else
  for (let attempt = 0; attempt < 8; attempt++) {
    const spin = rng() * 2 * Math.PI;
    const cand: { q: number; r: number }[] = [];
    for (let i = 0; i < N; i++) {
      const a = startAngle + spin + (2 * Math.PI * ((i + rot) % N)) / N + (rng() - 0.5) * 0.24;
      const rad = Rpix * (0.85 + rng() * 0.3);
      cand.push(pixelToAxial(rad * Math.cos(a), rad * Math.sin(a), 1));
    }
    const minPair = Math.min(
      ...cand.flatMap((c, i) => cand.slice(i + 1).map((d) => hexDistance({ q: c.q, r: c.r } as Region, { q: d.q, r: d.r } as Region))),
    );
    seeds = cand;
    if (N <= 1 || minPair >= adjacentSpacing - 1) break; // roomy enough: keep it (last attempt keeps regardless)
  }

  // 2. candidate hexes: an axial box covering every seed plus room for each island to grow. Only land
  // cells are ever rendered, so the box's shape (a parallelogram in pixel space) is never visible.
  const pad = Math.ceil(Math.sqrt(islandSize)) + 2;
  let minQ = Infinity;
  let maxQ = -Infinity;
  let minR = Infinity;
  let maxR = -Infinity;
  for (const s of seeds) {
    if (s.q < minQ) minQ = s.q;
    if (s.q > maxQ) maxQ = s.q;
    if (s.r < minR) minR = s.r;
    if (s.r > maxR) maxR = s.r;
  }
  const cellSet = new Set<string>();
  for (let r = minR - pad; r <= maxR + pad; r++) {
    for (let q = minQ - pad; q <= maxQ + pad; q++) {
      cellSet.add(hexKey(q, r));
    }
  }

  // 3. grow islands (round-robin BFS, keeping a 1-hex sea buffer to other islands so they never merge)
  const islandOf = new Map<string, number>();
  const frontiers: { q: number; r: number }[][] = seeds.map((s, i) => {
    islandOf.set(hexKey(s.q, s.r), i);
    return [s];
  });
  const sizes = new Array<number>(N).fill(1);
  // SHAPE BIAS: each island rolls its own elongation axis and strength; candidate neighbours that lie
  // along the axis are preferred, so one seed grows round atolls, another long reefs. Purely cosmetic:
  // tile counts stay equal and the buffer rule is untouched.
  const shapeAxis = variety ? Array.from({ length: N }, () => rng() * 2 * Math.PI) : [];
  const shapeBias = variety ? Array.from({ length: N }, () => 0.25 + rng() * 0.55) : []; // 0.25 = nearly round, 0.8 = a spit
  let growing = true;
  while (growing) {
    growing = false;
    for (let i = 0; i < N; i++) {
      if (sizes[i]! >= islandSize) continue; // every island grows to the same size, equal footing
      const front = frontiers[i]!;
      let placed = false;
      for (let fi = 0; fi < front.length && !placed; fi++) {
        const h = front[fi]!;
        const nbrs = hexNeighbors(h.q, h.r);
        for (let k = nbrs.length - 1; k > 0; k--) {
          const j = Math.floor(rng() * (k + 1));
          const tmp = nbrs[k]!;
          nbrs[k] = nbrs[j]!;
          nbrs[j] = tmp;
        }
        // pull growth along the island's own axis: with probability = its bias, lead with the neighbour
        // most aligned to the axis instead of a pure shuffle
        if (variety && rng() < shapeBias[i]!) {
          const ax = Math.cos(shapeAxis[i]!);
          const ay = Math.sin(shapeAxis[i]!);
          nbrs.sort((m, n2) => {
            const dm = (m.q - h.q) * ax + (m.r - h.r) * ay;
            const dn = (n2.q - h.q) * ax + (n2.r - h.r) * ay;
            return Math.abs(dn) - Math.abs(dm);
          });
        }
        for (const n of nbrs) {
          const key = hexKey(n.q, n.r);
          if (!cellSet.has(key) || islandOf.has(key)) continue;
          const touchesOther = hexNeighbors(n.q, n.r).some((m) => {
            const io = islandOf.get(hexKey(m.q, m.r));
            return io !== undefined && io !== i;
          });
          if (touchesOther) continue;
          islandOf.set(key, i);
          frontiers[i]!.push({ q: n.q, r: n.r });
          sizes[i]! += 1;
          placed = true;
          growing = true;
          break;
        }
      }
    }
  }

  // 3.5 land maps: fill the middle with the neutral WILDS province, turning the archipelago layout
  // into one connected mainland. (a) a heart blob grows from the map centre with NO buffer, so it may
  // touch the home provinces directly (that contact IS the land bridge); (b) a corridor is carved from
  // the centre to every province seed, guaranteeing connectivity even where the heart grew lopsided.
  const WILDS = N;
  const roadKeys = new Set<string>(); // land maps: the carved corridor lines become visible ROADS
  if (mapKind === 'land') {
    const c0 = pixelToAxial(0, 0, 1);
    const claim = (q: number, r: number): boolean => {
      const key = hexKey(q, r);
      if (!cellSet.has(key) || islandOf.has(key)) return false;
      islandOf.set(key, WILDS);
      return true;
    };
    const heartFrontier: { q: number; r: number }[] = [];
    if (claim(c0.q, c0.r)) heartFrontier.push(c0);
    let heartSize = heartFrontier.length;
    while (heartSize < islandSize && heartFrontier.length) {
      let placed = false;
      for (let fi = 0; fi < heartFrontier.length && !placed; fi++) {
        const h = heartFrontier[fi]!;
        const nbrs = hexNeighbors(h.q, h.r);
        for (let k = nbrs.length - 1; k > 0; k--) {
          const j = Math.floor(rng() * (k + 1));
          const tmp = nbrs[k]!;
          nbrs[k] = nbrs[j]!;
          nbrs[j] = tmp;
        }
        for (const n of nbrs) {
          if (!claim(n.q, n.r)) continue;
          heartFrontier.push({ q: n.q, r: n.r });
          heartSize += 1;
          placed = true;
          break;
        }
      }
      if (!placed) break; // boxed in (tiny maps): the corridors below still connect everything
    }
    for (const s of seeds) {
      for (const cell of hexLine(c0, s)) {
        claim(cell.q, cell.r);
        roadKeys.add(hexKey(cell.q, cell.r)); // the spoke: a king's road from the crossroads to each capital
      }
    }
    // rim roads: carve a corridor between each pair of NEIGHBOURING provinces too, so the continent is a
    // wheel (spokes + rim), not a star. A star funnels every war through one central chokepoint, and
    // measured all-AI runs stalemated to the round cap there; the rim gives each border a second front.
    // (rim corridors are carved as plain walkable ground, NOT roads: only the spokes carry the road
    // art. Stamping the rim too wove a dense lattice on small maps that read as a basket, not a road.)
    for (let i = 0; i < N; i++) {
      for (const cell of hexLine(seeds[i]!, seeds[(i + 1) % N]!)) claim(cell.q, cell.r);
    }
    // one dilation pass: any open cell touching 3+ land cells joins the wilds. Widens the 1-hex corridors
    // into marchable ground and rounds the coastline, without flooding the map into a rectangle.
    const grown: Array<[number, number]> = [];
    for (const key of cellSet) {
      if (islandOf.has(key)) continue;
      const parts = key.split(',');
      const q = Number(parts[0]);
      const r = Number(parts[1]);
      let landNbrs = 0;
      for (const n of hexNeighbors(q, r)) if (islandOf.has(hexKey(n.q, n.r))) landNbrs++;
      if (landNbrs >= 3) grown.push([q, r]);
    }
    for (const [q, r] of grown) islandOf.set(hexKey(q, r), WILDS);
  }

  // 4. build regions. Walkable adjacency: on isles maps only SAME-island neighbours (the sea separates
  // islands); on a land map every adjacent land tile is walkable regardless of province.
  const regionByKey = new Map<string, number>();
  const regions: Region[] = [];
  for (const [key, island] of islandOf) {
    const parts = key.split(',');
    const q = Number(parts[0]);
    const r = Number(parts[1]);
    const id = regions.length;
    regionByKey.set(key, id);
    regions.push({ id, q, r, island, owner: NEUTRAL, isCapital: false, neighbors: [], buildings: [], production: null, unit: null });
  }
  for (const reg of regions) {
    for (const n of hexNeighbors(reg.q, reg.r)) {
      const nid = regionByKey.get(hexKey(n.q, n.r));
      if (nid !== undefined && (mapKind === 'land' || regions[nid]!.island === reg.island)) reg.neighbors.push(nid);
    }
  }

  // 5. islands + capitals + resources (capital = the seed hex at the island's centre). On land maps the
  // entries are PROVINCES; index === faction id still holds, and the wilds append as the extra entry N.
  const islands: Island[] = [];
  for (let i = 0; i < N; i++) {
    const regs = regions.filter((reg) => reg.island === i).map((reg) => reg.id);
    const capId = regionByKey.get(hexKey(seeds[i]!.q, seeds[i]!.r));
    if (capId !== undefined) regions[capId]!.isCapital = true;
    islands.push({ id: i, regions: regs, resources: RES_COMBOS[(i + rot) % RES_COMBOS.length]!, faction: i, seaLinks: [] });
  }
  if (mapKind === 'land') {
    const regs = regions.filter((reg) => reg.island === WILDS).map((reg) => reg.id);
    islands.push({ id: WILDS, regions: regs, resources: WILDS_RESOURCES, faction: NEUTRAL, seaLinks: [] });

    // roads: stamp the carved corridor lines (capitals keep their own art; the road runs up to them)
    for (const reg of regions) if (roadKeys.has(hexKey(reg.q, reg.r)) && !reg.isCapital) reg.terrain = 'road';

    // mountains: deterministic cliff clusters in the wilds for tactical texture. Never on roads or beside
    // a capital, and a cluster only commits if stripping it keeps every remaining walkable tile connected
    // (the roads themselves guarantee the trunk network survives).
    const strip = (reg: Region): RegionId[] => {
      const links = [...reg.neighbors];
      for (const nId of links) {
        const n = regions[nId]!;
        n.neighbors = n.neighbors.filter((x) => x !== reg.id);
      }
      reg.neighbors = [];
      return links;
    };
    const restore = (reg: Region, links: RegionId[]): void => {
      reg.neighbors = links;
      for (const nId of links) {
        const n = regions[nId]!;
        if (!n.neighbors.includes(reg.id)) n.neighbors.push(reg.id);
      }
    };
    const connectedWithout = (): boolean => {
      const start = regions.find((r) => r.isCapital && !r.terrain);
      if (!start) return false;
      const seen = new Set<RegionId>();
      const stack = [start.id];
      while (stack.length) {
        const id = stack.pop()!;
        if (seen.has(id)) continue;
        seen.add(id);
        for (const n of regions[id]!.neighbors) stack.push(n);
      }
      return regions.every((r) => r.terrain === 'mountain' || seen.has(r.id));
    };
    const nearCapital = (reg: Region): boolean => regions.some((c) => c.isCapital && hexDistance(reg, c) <= 1);
    const candidates = regions.filter((r) => r.island === WILDS && !r.terrain && !nearCapital(r));
    for (const seedReg of candidates) {
      if (seedReg.terrain || rng() >= 0.1) continue;
      // grow a 2-4 tile ridge from this cell over eligible wilds ground
      const cluster: Region[] = [seedReg];
      let cur = seedReg;
      const want = 2 + Math.floor(rng() * 3);
      while (cluster.length < want) {
        const next = cur.neighbors
          .map((n) => regions[n]!)
          .find((n) => n.island === WILDS && !n.terrain && !n.isCapital && !nearCapital(n) && !cluster.includes(n));
        if (!next) break;
        cluster.push(next);
        cur = next;
      }
      const undo: Array<[Region, RegionId[]]> = [];
      for (const reg of cluster) {
        reg.terrain = 'mountain';
        undo.push([reg, strip(reg)]);
      }
      if (!connectedWithout()) {
        for (const [reg, links] of undo.reverse()) {
          delete reg.terrain;
          restore(reg, links);
        }
      }
    }
    return { regions, islands }; // one mainland: no sea, no sea links
  }

  // 6. sea-link graph: a ring around the polygon. Each island is sea-linked to its two neighbours on the
  // polygon, the two physically nearest islands, matching what the eye sees. For 3 factions this already
  // connects all three; for 4/5 it is a positional cycle (reaching an island across the map means going
  // through a neighbour). The ring is always connected, so no island is ever unreachable.
  const linkSets: Set<number>[] = Array.from({ length: N }, () => new Set<number>());
  for (let i = 0; i < N; i++) {
    const j = (i + 1) % N;
    if (i === j) continue;
    linkSets[i]!.add(j);
    linkSets[j]!.add(i);
  }
  for (let i = 0; i < N; i++) islands[i]!.seaLinks = [...linkSets[i]!].sort((a, b) => a - b);

  // 7. island cliffs (OPT-IN, curated free-play boards only). FAIRNESS RULE: every HOME island gets
  // the SAME number of mountain tiles (a board rolls K = 0..2 and applies it to all), because "my
  // island has two crags and yours has none" reads as a rigged start. If any island cannot host K
  // (connectivity or its 3-clear-coast floor would break), the whole board steps down to K-1, all the
  // way to flat: symmetric or nothing. The heart isle (below) is EXEMPT: it is nobody's start, and its
  // rough ground is the point.
  if (cliffs && islandSize >= 8) {
    const landKeysAll = (): Set<string> => new Set(regions.map((r) => hexKey(r.q, r.r)));
    const placeK = (K: number): boolean => {
      if (K === 0) return true;
      const undoAll: Array<[Region, RegionId[]]> = [];
      const rollback = (): void => {
        for (const [reg, links] of undoAll.reverse()) {
          delete reg.terrain;
          reg.neighbors = links;
          for (const nId of links) {
            const n = regions[nId]!;
            if (!n.neighbors.includes(reg.id)) n.neighbors.push(reg.id);
          }
        }
      };
      for (const isl of islands) {
        if (isl.faction === NEUTRAL) continue; // the heart isle rolls its own crags
        const mine = isl.regions.map((id) => regions[id]!);
        const cap = mine.find((r) => r.isCapital);
        const nearCap = (reg: Region): boolean => !!cap && hexDistance(reg, cap) <= 1;
        const options = mine.filter((r) => !r.isCapital && !nearCap(r) && !r.terrain);
        if (options.length < K) {
          rollback();
          return false;
        }
        const seedReg = options[Math.floor(rng() * options.length)]!;
        const cluster: Region[] = [seedReg];
        if (K > 1) {
          const next = seedReg.neighbors.map((n) => regions[n]!).find((n) => n.island === isl.id && !n.isCapital && !nearCap(n) && !n.terrain);
          if (next) cluster.push(next);
          else {
            const alt = options.find((o) => o.id !== seedReg.id);
            if (alt) cluster.push(alt);
          }
        }
        if (cluster.length < K) {
          rollback();
          return false;
        }
        for (const reg of cluster) {
          reg.terrain = 'mountain';
          const links = [...reg.neighbors];
          for (const nId of links) {
            const n = regions[nId]!;
            n.neighbors = n.neighbors.filter((x) => x !== reg.id);
          }
          reg.neighbors = [];
          undoAll.push([reg, links]);
        }
        const walkable = mine.filter((r) => !r.terrain || r.terrain === 'road');
        const seen = new Set<RegionId>();
        if (cap && !cap.terrain) {
          const stack = [cap.id];
          while (stack.length) {
            const id = stack.pop()!;
            if (seen.has(id)) continue;
            seen.add(id);
            for (const n of regions[id]!.neighbors) stack.push(n);
          }
        }
        const connected = walkable.every((r) => seen.has(r.id));
        const lk = landKeysAll();
        const clearCoasts = walkable.filter((r) => hexNeighbors(r.q, r.r).some((n) => !lk.has(hexKey(n.q, n.r)))).length;
        if (!connected || clearCoasts < 3) {
          rollback();
          return false;
        }
      }
      return true;
    };
    const K = Math.floor(rng() * 3); // 0, 1 or 2 crags per home island, one roll per BOARD
    if (!placeK(K)) if (!placeK(Math.max(0, K - 1))) placeK(0);
  }

  // 8. the HEART ISLE (opt-in, curated boards): a neutral island at the centre of the wheel, nobody's
  // start and everybody's prize. Deliberately SMALLER than the homes (about half + 1), so it enriches
  // the middle without bloating the map, yet stays big enough to be a real conquest. Wilds resources,
  // its own rough ground (1-2 crags, connectivity-checked), and sea links to EVERY home island, so the
  // race for the middle is open from the first harbor.
  if (heart && mapKind === 'isles') {
    const H = N;
    const heartSize = Math.floor(islandSize / 2) + 1; // homes 8/10/12 -> centre 5/6/7
    const centre = pixelToAxial(0, 0, 1);
    const taken = new Set(regions.map((r) => hexKey(r.q, r.r)));
    const mine: { q: number; r: number }[] = [];
    const bufferClear = (q: number, r: number): boolean => hexNeighbors(q, r).every((n) => {
      const owner = islandOf.get(hexKey(n.q, n.r));
      return owner === undefined || owner === H;
    });
    const startKey = hexKey(centre.q, centre.r);
    if (!taken.has(startKey) && bufferClear(centre.q, centre.r)) {
      mine.push(centre);
      islandOf.set(startKey, H);
      const frontier = [centre];
      while (mine.length < heartSize && frontier.length) {
        const h = frontier[Math.floor(rng() * frontier.length)]!;
        const nbrs = hexNeighbors(h.q, h.r);
        for (let k = nbrs.length - 1; k > 0; k--) {
          const j = Math.floor(rng() * (k + 1));
          const tmp = nbrs[k]!;
          nbrs[k] = nbrs[j]!;
          nbrs[j] = tmp;
        }
        let grew = false;
        for (const n of nbrs) {
          const key = hexKey(n.q, n.r);
          if (taken.has(key) || islandOf.get(key) !== undefined || !bufferClear(n.q, n.r)) continue;
          islandOf.set(key, H);
          mine.push(n);
          frontier.push(n);
          grew = true;
          break;
        }
        if (!grew) frontier.splice(frontier.indexOf(h), 1);
      }
    }
    if (mine.length >= Math.max(4, heartSize - 1)) {
      const baseId = regions.length;
      const ids: RegionId[] = [];
      for (const cell of mine) {
        const id = regions.length;
        regions.push({ id, q: cell.q, r: cell.r, island: H, owner: NEUTRAL, isCapital: false, neighbors: [], buildings: [], production: null, unit: null });
        ids.push(id);
      }
      // wire intra-island neighbours (isles rule: adjacency never crosses water)
      const byKey = new Map(regions.map((r) => [hexKey(r.q, r.r), r.id] as const));
      for (const id of ids) {
        const reg = regions[id]!;
        for (const n of hexNeighbors(reg.q, reg.r)) {
          const nid = byKey.get(hexKey(n.q, n.r));
          if (nid !== undefined && regions[nid]!.island === H) reg.neighbors.push(nid);
        }
      }
      // its rough ground: 1-2 crags (a small isle takes only 1), never disconnecting the isle. The rng
      // draw happens unconditionally so the stream stays size-independent
      const want = Math.min(mine.length >= 6 ? 2 : 1, 1 + Math.floor(rng() * 2));
      let placedCrags = 0;
      // Fisher-Yates, NOT sort(() => rng()-0.5): a random comparator is engine-defined in both order and
      // rng draw count, and this board must be identical on every JS engine
      const cragOrder = [...ids];
      for (let k = cragOrder.length - 1; k > 0; k--) {
        const j = Math.floor(rng() * (k + 1));
        const tmp = cragOrder[k]!;
        cragOrder[k] = cragOrder[j]!;
        cragOrder[j] = tmp;
      }
      for (const id of cragOrder) {
        if (placedCrags >= want) break;
        const reg = regions[id]!;
        if (reg.terrain) continue;
        const links = [...reg.neighbors];
        reg.terrain = 'mountain';
        for (const nId of links) {
          const n = regions[nId]!;
          n.neighbors = n.neighbors.filter((x) => x !== reg.id);
        }
        reg.neighbors = [];
        const walk = ids.map((x) => regions[x]!).filter((r) => !r.terrain);
        const seen = new Set<RegionId>();
        if (walk.length) {
          const stack = [walk[0]!.id];
          while (stack.length) {
            const cur = stack.pop()!;
            if (seen.has(cur)) continue;
            seen.add(cur);
            for (const n of regions[cur]!.neighbors) stack.push(n);
          }
        }
        if (walk.every((r) => seen.has(r.id))) placedCrags++;
        else {
          delete reg.terrain;
          reg.neighbors = links;
          for (const nId of links) {
            const n = regions[nId]!;
            if (!n.neighbors.includes(reg.id)) n.neighbors.push(reg.id);
          }
        }
      }
      void baseId;
      islands.push({ id: H, regions: ids, resources: WILDS_RESOURCES, faction: NEUTRAL, seaLinks: [] });
      // open water to every home: the prize is reachable from all sides
      for (let i = 0; i < N; i++) {
        islands[i]!.seaLinks = [...new Set([...islands[i]!.seaLinks, H])].sort((a2, b2) => a2 - b2);
        islands[H]!.seaLinks.push(i);
      }
      islands[H]!.seaLinks.sort((a2, b2) => a2 - b2);
    }
  }

  return { regions, islands };
}
